import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Inject,
  Input,
  OnInit,
  Optional,
  Output,
} from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipInputEvent, MatChipsModule } from '@angular/material/chips';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import type { CaseDataField } from '@protocols/domain';

export type CaseDataFieldType = CaseDataField['type'];

/**
 * Selectable field-kind variants, in display order, for the create-mode type
 * picker. `case_name` is deliberately absent: that field is always seeded
 * automatically on every case type, so offering it here would only allow
 * unremovable duplicates.
 */
export const CASE_DATA_FIELD_TYPES: CaseDataFieldType[] = [
  'textedit',
  'number',
  'boolean',
  'selection',
  'switcher',
  'date',
  'location',
  'label',
  'sectionHeader',
];

/**
 * MatDialog payload. `field === null` opens the dialog in create mode.
 * `lockName` freezes the field's internal name (see {@link
 * CaseDataFieldEditorDialogComponent.lockName}); defaults to unlocked.
 */
export interface CaseDataFieldEditorDialogData {
  field: CaseDataField | null;
  lockName?: boolean;
}

interface CaseDataFieldForm {
  type: FormControl<CaseDataFieldType>;
  displayName: FormControl<string>;
  required: FormControl<boolean>;
  encrypted: FormControl<boolean>;
  // textedit
  multiline: FormControl<boolean>;
  placeholder: FormControl<string>;
  maxLines: FormControl<number | null>;
  maxLength: FormControl<number | null>;
  // number
  decimals: FormControl<number | null>;
  min: FormControl<number | null>;
  max: FormControl<number | null>;
  units: FormControl<string>;
  // boolean
  default: FormControl<boolean>;
  // selection / switcher
  options: FormControl<string[]>;
  multiple: FormControl<boolean>;
}

/**
 * Dumb editor for a single {@link CaseDataField}. Mirrors the old
 * `field-type-editor`, but is driven by `@Input`/`@Output` (no
 * `ControlValueAccessor`, no DB-backed presets) and is always shown in a dialog:
 * the consumer opens it with `MAT_DIALOG_DATA` and reads the produced field from
 * `afterClosed()`; the `@Input`/`@Output` surface keeps it testable / usable
 * outside a dialog (stories).
 *
 * The type itself is the source of truth for which detail controls apply, so the
 * per-variant sections are toggled on the selected `type`. Buttons sit at the
 * bottom, 50% each (asset-field-dialog parity).
 */
@Component({
  selector: 'ts-case-data-field-editor-dialog',
  templateUrl: './case-data-field-editor-dialog.component.html',
  styleUrls: ['./case-data-field-editor-dialog.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    MatDialogModule,
    MatFormFieldModule,
    MatCheckboxModule,
    MatChipsModule,
    MatIconModule,
  ],
})
export class CaseDataFieldEditorDialogComponent implements OnInit {
  /** The field to edit; `null`/absent opens create mode. */
  @Input() field: CaseDataField | null = null;

  /**
   * Freezes the field's internal name: when `true`, the internal-name preview
   * and the value produced on save both keep the original identifier while the
   * display name is edited. Set by the consumer for fields that already belong
   * to a persisted case type; a create/add always leaves it `false`.
   */
  @Input() lockName = false;

  @Output() saved = new EventEmitter<CaseDataField>();
  @Output() cancelled = new EventEmitter<void>();

  public readonly types = CASE_DATA_FIELD_TYPES;
  public readonly separatorKeysCodes = [ENTER, COMMA];

  public isNew = true;
  public form!: FormGroup<CaseDataFieldForm>;

  private initialField: CaseDataField | null = null;

  constructor(
    @Optional()
    public readonly dialogRef: MatDialogRef<CaseDataFieldEditorDialogComponent, CaseDataField | undefined> | null,
    @Optional() @Inject(MAT_DIALOG_DATA) private readonly data: CaseDataFieldEditorDialogData | null
  ) {}

  ngOnInit(): void {
    if (this.data) {
      this.initialField = this.data.field;
      this.lockName = this.data.lockName ?? false;
    } else {
      this.initialField = this.field;
    }
    this.isNew = !this.initialField;
    this.buildForm(this.initialField);
  }

  public get selectedType(): CaseDataFieldType {
    return this.form.controls.type.value;
  }

  /**
   * The internal name previewed to the user — the exact value that will be
   * persisted (see `resolveName`), so the preview can never disagree with save.
   */
  public get internalName(): string {
    return this.resolveName(this.selectedType, this.form.controls.displayName.value);
  }

  public get canSave(): boolean {
    if (this.form.controls.displayName.value.trim() === '') return false;
    if (this.needsOptions(this.selectedType) && this.form.controls.options.value.length === 0) return false;
    return true;
  }

  public showsRequiredAndEncrypted(type: CaseDataFieldType): boolean {
    // The label variant carries no required/encrypted semantics (old-editor parity).
    return type !== 'label';
  }

  public needsOptions(type: CaseDataFieldType): boolean {
    return type === 'selection' || type === 'switcher';
  }

  public addOption(event: MatChipInputEvent): void {
    const value = (event.value ?? '').trim();
    if (value && !this.form.controls.options.value.includes(value)) {
      this.form.controls.options.setValue([...this.form.controls.options.value, value]);
      this.form.markAsDirty();
    }
    event.chipInput?.clear();
  }

  public removeOption(option: string): void {
    this.form.controls.options.setValue(this.form.controls.options.value.filter((o) => o !== option));
    this.form.markAsDirty();
  }

  public save(): void {
    if (!this.canSave) return;
    const field = this.buildField();
    this.saved.emit(field);
    this.dialogRef?.close(field);
  }

  public cancel(): void {
    this.cancelled.emit();
    this.dialogRef?.close(undefined);
  }

  private buildForm(field: CaseDataField | null): void {
    const f = field as CaseDataField | (Partial<CaseDataField> & { [key: string]: unknown }) | null;
    const numberish = (value: unknown): number | null => (typeof value === 'number' ? value : null);

    this.form = new FormGroup<CaseDataFieldForm>({
      type: new FormControl<CaseDataFieldType>(field?.type ?? 'textedit', { nonNullable: true }),
      displayName: new FormControl<string>(field?.displayName ?? '', { nonNullable: true }),
      // The case-name field is required by definition: ignore the stored value
      // (legacy data may carry `false`) and lock the control.
      required: new FormControl<boolean>(
        field?.type === 'case_name'
          ? { value: true, disabled: true }
          : field?.required ?? false,
        { nonNullable: true }
      ),
      encrypted: new FormControl<boolean>(field?.encrypted ?? false, { nonNullable: true }),
      multiline: new FormControl<boolean>(Boolean((f as { multiline?: boolean })?.multiline), { nonNullable: true }),
      placeholder: new FormControl<string>((f as { placeholder?: string })?.placeholder ?? '', { nonNullable: true }),
      maxLines: new FormControl<number | null>(numberish((f as { maxLines?: number })?.maxLines)),
      maxLength: new FormControl<number | null>(numberish((f as { maxLength?: number })?.maxLength)),
      decimals: new FormControl<number | null>(numberish((f as { decimals?: number })?.decimals)),
      min: new FormControl<number | null>(numberish((f as { min?: number })?.min)),
      max: new FormControl<number | null>(numberish((f as { max?: number })?.max)),
      units: new FormControl<string>((f as { units?: string })?.units ?? '', { nonNullable: true }),
      default: new FormControl<boolean>(Boolean((f as { default?: boolean })?.default), { nonNullable: true }),
      options: new FormControl<string[]>([...((f as { options?: string[] })?.options ?? [])], { nonNullable: true }),
      multiple: new FormControl<boolean>(Boolean((f as { multiple?: boolean })?.multiple), { nonNullable: true }),
    });
  }

  /**
   * The single source of truth for the field's internal name, shared by the
   * `internalName` preview and `buildField` so they can never diverge: a locked
   * field keeps its original identifier (existing cases reference it), otherwise
   * the name is derived from the display name (`case_name` always maps to the
   * fixed 'case_identifier' via `generateName`).
   */
  private resolveName(type: CaseDataFieldType, displayName: string): string {
    return this.lockName && this.initialField !== null
      ? this.initialField.name
      : this.generateName(type, displayName);
  }

  private generateName(type: CaseDataFieldType, displayName: string): string {
    if (type === 'case_name') return 'case_identifier';
    const trimmed = displayName.trim();
    if (!trimmed) return '';
    return trimmed
      .toLowerCase()
      .replace(/[\s?¡¿!*:,./()[\]-]+/g, '_')
      .replace(/[àáâä]/gi, 'a')
      .replace(/[èéêë]/gi, 'e')
      .replace(/[ìíîï]/gi, 'i')
      .replace(/[òóôö]/gi, 'o')
      .replace(/[ùúûü]/gi, 'u');
  }

  private buildField(): CaseDataField {
    const type = this.selectedType;
    const v = this.form.getRawValue();
    const name = this.resolveName(type, v.displayName);
    const base = {
      name,
      displayName: v.displayName.trim(),
      // case_name is required by definition, whatever the form (or legacy data) says.
      required: type === 'case_name' ? true : this.showsRequiredAndEncrypted(type) ? v.required : false,
      encrypted: this.showsRequiredAndEncrypted(type) ? v.encrypted : false,
    };

    switch (type) {
      case 'textedit':
        return {
          ...base,
          type,
          multiline: v.multiline,
          ...(v.placeholder.trim() ? { placeholder: v.placeholder.trim() } : {}),
          ...(v.maxLines !== null ? { maxLines: v.maxLines } : {}),
          ...(v.maxLength !== null ? { maxLength: v.maxLength } : {}),
        };
      case 'number':
        return {
          ...base,
          type,
          ...(v.decimals !== null ? { decimals: v.decimals } : {}),
          ...(v.min !== null ? { min: v.min } : {}),
          ...(v.max !== null ? { max: v.max } : {}),
          ...(v.units.trim() ? { units: v.units.trim() } : {}),
        };
      case 'boolean':
        return { ...base, type, default: v.default };
      case 'selection':
        return { ...base, type, options: [...v.options], multiple: v.multiple };
      case 'switcher':
        return { ...base, type, options: [...v.options], multiple: v.multiple };
      default:
        // sectionHeader | label | case_name | date | location — base only.
        return { ...base, type } as CaseDataField;
    }
  }
}
